import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { companyThemeApi, type CompanyThemeApi } from '../../services/api';
import { apiErrorCode, saveErrorText } from '../../utils/apiError';
import {
  contrastAgainstWhite,
  DEFAULT_PALETTE_KEY,
  DEFAULT_THEME_COLORS,
  isHexColor,
  LOGO_ACCEPT,
  LOGO_ERROR_CODES,
  MAX_LOGO_SIZE_BYTES,
  MIN_CONTRAST,
  paletteFor,
  suggestReadableColor,
  THEME_COLOR_FIELDS,
  THEME_PALETTES,
  type CompanyTheme,
  type ThemeColorField,
  type ThemeColors,
} from './companyTheme';
import { CompanyThemePreview } from './CompanyThemePreview';
import { useThemeLogo } from './useThemeLogo';

interface CompanyThemeEditorProps {
  /** Which endpoints to use: the signed-in company's own, or admin's. */
  api?: 'company' | 'admin';
  /** Required for `api="admin"`. */
  companyId?: string;
  /**
   * Any other owner of a theme, e.g. a lawyer or an inspector (ClickUp
   * 869fa6m3u): the same editor against their endpoints. Wins over `api`.
   */
  client?: CompanyThemeApi;
  /** What the theme looks like where others see it; a contract by default. */
  renderPreview?: (preview: { colors: ThemeColors; name: string; logoSrc: string | null }) => React.ReactNode;
  /** The line under the colours about where the theme is used. */
  infoText?: string;
}

type Message = { type: 'success' | 'error'; text: string };

const colorsOf = (theme: CompanyTheme): ThemeColors => ({
  headingColor: theme.headingColor.toUpperCase(),
  textColor: theme.textColor.toUpperCase(),
  accentColor: theme.accentColor.toUpperCase(),
});

/**
 * Logo and colours for the company's contracts (Inställningar → Tema and the
 * admin tab "Tema"). The logo is stored on upload; colours are saved with
 * "Spara tema".
 */
export function CompanyThemeEditor({ api = 'company', companyId, client: injectedClient, renderPreview, infoText }: CompanyThemeEditorProps) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const client: CompanyThemeApi = useMemo(
    () => injectedClient ?? (api === 'admin' ? companyThemeApi.forAdmin(companyId ?? '') : companyThemeApi.forCompany()),
    [injectedClient, api, companyId]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saved, setSaved] = useState<CompanyTheme | null>(null);
  const [colors, setColors] = useState<ThemeColors>(DEFAULT_THEME_COLORS);
  // What is typed in the hex fields; applied to `colors` once it is a valid hex.
  const [hexDrafts, setHexDrafts] = useState<ThemeColors>(DEFAULT_THEME_COLORS);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { src: logoSrc } = useThemeLogo(saved?.logoUrl);

  const applySaved = useCallback((theme: CompanyTheme) => {
    setSaved(theme);
    setColors(colorsOf(theme));
    setHexDrafts(colorsOf(theme));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    client
      .get()
      .then(theme => { if (!cancelled) applySaved(theme); })
      .catch(() => { if (!cancelled) setLoadFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client, applySaved]);

  const savedColors = saved ? colorsOf(saved) : DEFAULT_THEME_COLORS;
  const selectedPalette = paletteFor(colors);
  const paletteKey = selectedPalette?.key ?? null;
  // The palette follows from the colours, so comparing colours is enough.
  const dirty = THEME_COLOR_FIELDS.some(
    field => colors[field] !== savedColors[field] || hexDrafts[field] !== colors[field]
  );
  // Only a complete #RRGGBB blocks saving. Low contrast is a recommendation:
  // the company may keep its own colour (ClickUp 869fa66kk).
  const fieldValid = (field: ThemeColorField) => hexDrafts[field] === colors[field] && isHexColor(colors[field]);
  const canSave = !saving && dirty && THEME_COLOR_FIELDS.every(fieldValid);

  // Warn before leaving the page with unsaved colours.
  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  const ratioText = (ratio: number) =>
    new Intl.NumberFormat(i18n.language === 'en' ? 'en-GB' : 'sv-SE', { maximumFractionDigits: 1 }).format(
      Math.floor(ratio * 10) / 10
    );

  const setColor = (field: ThemeColorField, value: string) => {
    const upper = value.toUpperCase();
    setHexDrafts(previous => ({ ...previous, [field]: upper }));
    if (isHexColor(upper)) setColors(previous => ({ ...previous, [field]: upper }));
    setMessage(null);
  };

  const choosePalette = (key: string) => {
    const palette = THEME_PALETTES.find(item => item.key === key);
    if (!palette) return;
    const next = { headingColor: palette.headingColor, textColor: palette.textColor, accentColor: palette.accentColor };
    setColors(next);
    setHexDrafts(next);
    setMessage(null);
  };

  const cancel = () => {
    setColors(savedColors);
    setHexDrafts(savedColors);
    setMessage(null);
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setMessage(null);
    try {
      applySaved(await client.update({ ...colors, paletteKey }));
      setMessage({ type: 'success', text: t('settings.theme.saved') });
    } catch (error) {
      setMessage({ type: 'error', text: saveErrorText(t, error, 'settings.theme.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const logoErrorText = (error: unknown) => {
    const code = apiErrorCode(error);
    return code && (LOGO_ERROR_CODES as readonly string[]).includes(code)
      ? t(`settings.theme.logo.errors.${code}`)
      : t('settings.theme.logo.errors.generic');
  };

  const uploadLogo = async (file: File | undefined) => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setLogoError(null);
    setMessage(null);
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setLogoError(t('settings.theme.logo.errors.LOGO_UNSUPPORTED_TYPE'));
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setLogoError(t('settings.theme.logo.errors.LOGO_TOO_LARGE'));
      return;
    }
    setLogoBusy(true);
    setUploadProgress(0);
    try {
      const theme = await client.uploadLogo(file, setUploadProgress);
      // Keep unsaved colour edits; only the logo changed on the server.
      setSaved(theme);
      setMessage({ type: 'success', text: t('settings.theme.logo.uploaded') });
    } catch (error) {
      setLogoError(logoErrorText(error));
    } finally {
      setLogoBusy(false);
      setUploadProgress(null);
    }
  };

  const removeLogo = async () => {
    setConfirmRemove(false);
    setLogoBusy(true);
    setLogoError(null);
    setMessage(null);
    try {
      setSaved(await client.removeLogo());
      setMessage({ type: 'success', text: t('settings.theme.logo.removed') });
    } catch {
      setLogoError(t('settings.theme.logo.errors.removeFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5" role="status">
        <span className="spinner-border text-primary" aria-hidden="true"></span>
        <span className="visually-hidden">{t('common.loading')}</span>
      </div>
    );
  }

  if (loadFailed || !saved) {
    return <div className="alert alert-danger mb-0">{t('settings.theme.loadError')}</div>;
  }

  const hasLogo = Boolean(saved.logoUrl);

  return (
    <div className="company-theme-editor" data-testid="company-theme-editor">
      {message && (
        <div
          className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-4`}
          role={message.type === 'success' ? 'status' : 'alert'}
        >
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label={t('common.close')}></button>
        </div>
      )}

      <div className="row g-4">
        <div className="col-lg-7 d-flex flex-column gap-4">
          <section className="company-theme-section" aria-labelledby={`${ids}-logo`}>
            <h3 id={`${ids}-logo`} className="company-theme-section-title">{t('settings.theme.logo.title')}</h3>
            <div className="company-theme-logo-box" data-testid="company-theme-logo">
              {hasLogo && logoSrc ? (
                <img src={logoSrc} alt={t('settings.theme.logo.alt', { company: saved.companyName })} className="company-theme-logo-img" />
              ) : hasLogo ? (
                <span className="spinner-border spinner-border-sm text-secondary" aria-hidden="true"></span>
              ) : (
                <div
                  className="company-theme-logo-placeholder"
                  style={{ '--company-theme-heading': colors.headingColor } as React.CSSProperties}
                >
                  <span className="company-theme-logo-placeholder-name">{saved.companyName}</span>
                  <span className="small text-muted">{t('settings.theme.logo.none')}</span>
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-primary company-theme-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={logoBusy}
              >
                <i className="feather-upload me-2" aria-hidden="true"></i>
                {hasLogo ? t('settings.theme.logo.replace') : t('settings.theme.logo.upload')}
              </button>
              {hasLogo && (
                <button
                  type="button"
                  className="btn btn-outline-danger company-theme-btn"
                  onClick={() => setConfirmRemove(true)}
                  disabled={logoBusy}
                >
                  <i className="feather-trash-2 me-2" aria-hidden="true"></i>
                  {t('settings.theme.logo.remove')}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept={LOGO_ACCEPT}
                onChange={event => void uploadLogo(event.target.files?.[0])}
                aria-label={hasLogo ? t('settings.theme.logo.replace') : t('settings.theme.logo.upload')}
                data-testid="company-theme-logo-input"
              />
            </div>
            <p className="form-text mb-0 mt-2">{t('settings.theme.logo.help')}</p>
            {uploadProgress !== null && (
              <div className="mt-3" role="status">
                <div
                  className="progress company-theme-progress"
                  role="progressbar"
                  aria-label={t('settings.theme.logo.uploadingAria')}
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <span className="small text-muted">{t('settings.theme.logo.uploading', { percent: uploadProgress })}</span>
              </div>
            )}
            {logoError && <div className="alert alert-danger mt-3 mb-0" role="alert">{logoError}</div>}
          </section>

          <section className="company-theme-section" aria-labelledby={`${ids}-colors`}>
            <h3 id={`${ids}-colors`} className="company-theme-section-title">{t('settings.theme.colors.title')}</h3>
            <p className="small fw-semibold mb-2" id={`${ids}-palettes`}>{t('settings.theme.colors.palettes')}</p>
            <div className="company-theme-palettes" role="group" aria-labelledby={`${ids}-palettes`}>
              {THEME_PALETTES.map(palette => (
                <button
                  key={palette.key}
                  type="button"
                  className={`company-theme-palette${paletteKey === palette.key ? ' is-selected' : ''}`}
                  aria-pressed={paletteKey === palette.key}
                  onClick={() => choosePalette(palette.key)}
                >
                  <span className="company-theme-swatches" aria-hidden="true">
                    {THEME_COLOR_FIELDS.map(field => (
                      <span
                        key={field}
                        className="company-theme-swatch"
                        style={{ '--company-theme-swatch': palette[field] } as React.CSSProperties}
                      ></span>
                    ))}
                  </span>
                  <span className="company-theme-palette-name">{t(`settings.theme.palettes.${palette.key}`)}</span>
                </button>
              ))}
            </div>

            <p className="small fw-semibold mt-4 mb-2">{t('settings.theme.colors.custom')}</p>
            <div className="d-flex flex-column gap-3">
              {THEME_COLOR_FIELDS.map(field => {
                const draft = hexDrafts[field];
                const draftValid = isHexColor(draft);
                const ratio = draftValid ? contrastAgainstWhite(draft) : 0;
                const ok = draftValid && ratio >= MIN_CONTRAST[field];
                const label = t(`settings.theme.fields.${field}`);
                const suggestion = draftValid && !ok ? suggestReadableColor(draft, MIN_CONTRAST[field]) : null;
                return (
                  <div key={field} className="company-theme-field">
                    <label htmlFor={`${ids}-${field}`} className="form-label fw-semibold mb-1">
                      {label}
                      {field === 'accentColor' && (
                        <span className="fw-normal text-muted"> · {t('settings.theme.fields.accentHint')}</span>
                      )}
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color company-theme-color-input"
                        value={colors[field].toLowerCase()}
                        onChange={event => setColor(field, event.target.value)}
                        aria-label={t('settings.theme.colors.pickerAria', { field: label })}
                      />
                      <input
                        id={`${ids}-${field}`}
                        type="text"
                        className={`form-control company-theme-hex-input${draftValid ? '' : ' is-invalid'}`}
                        value={draft}
                        maxLength={7}
                        spellCheck={false}
                        autoComplete="off"
                        onChange={event => setColor(field, event.target.value.trim())}
                        aria-describedby={`${ids}-${field}-contrast`}
                        aria-invalid={!draftValid || undefined}
                      />
                    </div>
                    <div
                      id={`${ids}-${field}-contrast`}
                      className={`company-theme-contrast ${!draftValid ? 'is-invalid' : ok ? 'is-ok' : 'is-advice'}`}
                      aria-live="polite"
                      data-testid={`company-theme-contrast-${field}`}
                    >
                      <i className={`feather-${ok ? 'check-circle' : 'info'} me-1`} aria-hidden="true"></i>
                      {!draftValid
                        ? t('settings.theme.contrast.invalid')
                        : ok
                          ? t('settings.theme.contrast.ok', { ratio: ratioText(ratio) })
                          : t('settings.theme.contrast.advice', {
                              ratio: ratioText(ratio),
                              minimum: ratioText(MIN_CONTRAST[field]),
                            })}
                    </div>
                    {suggestion && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm company-theme-suggestion mt-2"
                        onClick={() => setColor(field, suggestion)}
                        data-testid={`company-theme-suggestion-${field}`}
                      >
                        <span
                          className="company-theme-swatch"
                          style={{ '--company-theme-swatch': suggestion } as React.CSSProperties}
                          aria-hidden="true"
                        ></span>
                        {t('settings.theme.contrast.useSuggestion', {
                          color: suggestion,
                          ratio: ratioText(contrastAgainstWhite(suggestion)),
                        })}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className="btn btn-link company-theme-reset px-0 mt-3"
              onClick={() => choosePalette(DEFAULT_PALETTE_KEY)}
            >
              <i className="feather-rotate-ccw me-2" aria-hidden="true"></i>
              {t('settings.theme.colors.reset')}
            </button>
          </section>

          <p className="form-text mb-0">{infoText ?? t('settings.theme.info')}</p>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <button
              type="button"
              className="btn btn-primary company-theme-btn"
              onClick={() => void save()}
              disabled={!canSave}
            >
              {saving ? (
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
              ) : (
                <i className="feather-save me-2" aria-hidden="true"></i>
              )}
              {saving ? t('settings.theme.saving') : t('settings.theme.save')}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary company-theme-btn"
              onClick={cancel}
              disabled={!dirty || saving}
            >
              {t('common.cancel')}
            </button>
            {dirty && <span className="badge text-bg-warning">{t('settings.theme.unsaved')}</span>}
          </div>
        </div>

        <div className="col-lg-5">
          <div className="company-theme-preview-column">
            <h3 className="company-theme-section-title">{t('settings.theme.preview.title')}</h3>
            {renderPreview
              ? renderPreview({ colors, name: saved.companyName, logoSrc: hasLogo ? logoSrc ?? null : null })
              : <CompanyThemePreview colors={colors} companyName={saved.companyName} logoSrc={hasLogo ? logoSrc : null} />}
          </div>
        </div>
      </div>

      {confirmRemove && (
        <Modal
          title={t('settings.theme.logo.removeConfirm.title')}
          onClose={() => setConfirmRemove(false)}
          size="sm"
          footer={
            <>
              <button type="button" className="btn btn-outline-secondary company-theme-btn" onClick={() => setConfirmRemove(false)}>
                {t('settings.theme.logo.removeConfirm.cancel')}
              </button>
              <button type="button" className="btn btn-danger company-theme-btn" onClick={() => void removeLogo()}>
                {t('settings.theme.logo.removeConfirm.confirm')}
              </button>
            </>
          }
        >
          <p className="mb-0">{t('settings.theme.logo.removeConfirm.body')}</p>
        </Modal>
      )}
    </div>
  );
}

export default CompanyThemeEditor;
